import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * 社交媒体分享组件
 * @param {string} url - 要分享的URL
 * @param {string} title - 分享标题
 * @param {string} description - 分享描述
 * @param {string} image - 分享图片URL
 * @param {object} sx - 样式属性
 */
const SocialShare = ({ url, title, description, image, sx = {} }) => {
  const [copyDialog, setCopyDialog] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  
  // 如果没有提供URL，使用当前页面URL
  const shareUrl = url || window.location.href;
  
  // 编码分享文本
  const encodedTitle = encodeURIComponent(title || document.title);
  const encodedDesc = encodeURIComponent(description || '');
  const encodedUrl = encodeURIComponent(shareUrl);
  
  // 社交媒体分享链接
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
  
  // 复制链接处理
  const handleCopyLink = () => {
    setCopyDialog(true);
  };
  
  const handleClose = () => {
    setCopyDialog(false);
    // 重置复制状态
    setTimeout(() => setCopied(false), 300);
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('无法复制URL: ', err);
      });
  };
  
  return (
    <Box sx={{ ...sx }}>
      <Paper 
        elevation={0} 
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderRadius: 3,
          backgroundColor: 'rgba(0,0,0,0.03)',
          gap: 1
        }}
      >
        <Typography variant="body2" sx={{ mr: 1, fontWeight: 'medium' }}>
          分享:
        </Typography>
        
        <Tooltip title="分享到Facebook">
          <IconButton 
            aria-label="share to facebook" 
            onClick={() => window.open(facebookShareUrl, '_blank')}
            size="small"
            sx={{ color: '#3b5998' }}
          >
            <FacebookIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="分享到Twitter">
          <IconButton 
            aria-label="share to twitter" 
            onClick={() => window.open(twitterShareUrl, '_blank')}
            size="small"
            sx={{ color: '#1DA1F2' }}
          >
            <TwitterIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="分享到WhatsApp">
          <IconButton 
            aria-label="share to whatsapp" 
            onClick={() => window.open(whatsappShareUrl, '_blank')}
            size="small"
            sx={{ color: '#25D366' }}
          >
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="复制链接">
          <IconButton 
            aria-label="copy link" 
            onClick={handleCopyLink}
            size="small"
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
      
      {/* 复制链接对话框 */}
      <Dialog open={copyDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          分享链接
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            复制以下链接分享给好友:
          </Typography>
          <Box sx={{ display: 'flex', mt: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              value={shareUrl}
              size="small"
              InputProps={{
                readOnly: true,
              }}
            />
            <Button
              variant="contained"
              color={copied ? "success" : "primary"}
              onClick={copyToClipboard}
              startIcon={<ContentCopyIcon />}
              sx={{ ml: 1, minWidth: '100px' }}
            >
              {copied ? "已复制" : "复制"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>关闭</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialShare;